// Popcorn (popcorn.email), Peritus Learning's CRM: tells it which pages a
// visitor views, so it can see which organisations visit (from the IP address)
// and which of its email contacts come back (a Popcorn email link carries
// refPopcornContactId, kept in the browser for later visits). Requested by
// Peritus, Oct 2026; the data is Peritus Learning's.
//
// Their snippet, rebuilt so it may run here:
// * only with the visitor's "crm" consent (consent.js), which only Accept all
//   grants, the banner saying it lets Peritus Learning see which organisations
//   visit; never before a choice (UK PECR / UK GDPR), and only where
//   VUE_APP_POPCORN_ACCOUNT is set: the
//   staging site for now; production stays off until Popcorn's data
//   processing agreement is in place and Steve has approved the wording;
// * never on the purchase pages (/get-started and its order pages): people
//   type personal details there, and the order page's address carries the
//   key to the signed agreement;
// * the page address it sends is cleaned to origin, path and utm_* (the app's
//   Buy IMPROVE hand off puts a trial's email, name and company in the query);
// * every page view, not just the first: the site moves between pages without
//   reloading, so router.js calls this after each navigation;
// * left out: their postMessage listener for www.perituslearning.co.uk
//   (Peritus does not need contact passing).
//
// Plan and decisions: docs/improve.md in the tracker repo (Popcorn, Oct 2026).

import { hasCrm } from '@/utils/consent';

const ACCOUNT = process.env.VUE_APP_POPCORN_ACCOUNT || '';
const ENDPOINT = 'https://api.popcorn.email/v1/public/getip';
// Popcorn's own names: its email links set these, and its snippet stores the
// contact under the same key, so a visitor's history carries over.
const CONTACT = 'refPopcornContactId';
const EMAIL = 'refPopcornEmailId';
const SALES_ENGAGEMENT = 'refPopcornSE';
const PURCHASE_PAGES = /^\/get-started(\/|$)/;

let ran = false;
let lastScript = null;

// Switched on for this build (the cookie banner offers the choice only then).
export function popcornAvailable() {
  return !!ACCOUNT;
}

// A Popcorn script has run on this page load (it cannot be unloaded).
export function isPopcornLoaded() {
  return ran;
}

function param(search, name) {
  return new URLSearchParams(search || '').get(name);
}

function storage() {
  try {
    return window.localStorage;
  } catch (e) {
    return null; // blocked: no contact is remembered
  }
}

// The address Popcorn is told: origin and path, plus utm_* only.
export function cleanAddress(location) {
  const kept = new URLSearchParams();
  new URLSearchParams(location.search || '').forEach((value, key) => {
    if (/^utm_/i.test(key)) kept.append(key, value);
  });
  const query = kept.toString();
  return location.origin + location.pathname + (query ? '?' + query : '');
}

// Reports the page the visitor is on. Does nothing when switched off, without
// consent (scripts/prerender.js renders with none), or on a purchase page.
export function trackPopcorn() {
  if (!ACCOUNT || typeof window === 'undefined' || typeof document === 'undefined' || !hasCrm()) {
    return false;
  }
  const location = window.location;
  if (PURCHASE_PAGES.test(location.pathname)) return false;

  const store = storage();
  let contact = param(location.search, CONTACT);
  if (contact && store) store.setItem(CONTACT, contact);
  if (!contact && store) contact = store.getItem(CONTACT);

  // The values their snippet sends for an anonymous visitor (contactId=null,
  // emailId=0, isSE=false), so Popcorn reads ours exactly as it reads it.
  const query = [
    'id=' + encodeURIComponent(ACCOUNT),
    'tier=',
    'contactId=' + encodeURIComponent(contact || 'null'),
    'emailId=' + encodeURIComponent(param(location.search, EMAIL) || '0'),
    'isSE=' + encodeURIComponent(param(location.search, SALES_ENGAGEMENT) || 'false'),
    'ref=' + encodeURIComponent(cleanAddress(location)),
  ].join('&');

  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.async = true;
  script.src = ENDPOINT + '?' + query;
  if (lastScript && lastScript.parentNode) lastScript.parentNode.removeChild(lastScript);
  document.head.appendChild(script);
  lastScript = script;
  ran = true;
  return true;
}

// Consent withdrawn: forget the remembered contact. A script that already ran
// stays in the page; trackers.js reloads the page when that happened.
export function clearPopcorn() {
  const store = storage();
  if (store) store.removeItem(CONTACT);
}
